import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DOTS, usePagination } from '../../hooks/usePagination';
import { Button } from '../button/Button';
import { Typography } from '../typography';

// Interfaces

interface PaginationPropsBase {
  currentPage: number;
  totalCount: number;
  siblingCount?: number;
  pageSize: number;
  scrollToRef?: React.RefObject<HTMLInputElement>;
}

interface PaginationPropsWithFunction extends PaginationPropsBase {
  onPageChange: (pageNumber: number) => void;
  linkGenerator?: never;
}

interface PaginationPropsWithLink extends PaginationPropsBase {
  linkGenerator: (pageNumber: number) => string;
  onPageChange?: never;
}

type PaginationProps = PaginationPropsWithFunction | PaginationPropsWithLink;

// Styles

const StyledPagination = styled.nav`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.medium};
    justify-content: space-between;

    a {
      span {
        display: none;
      }
    }

    // Tablet > styles

    @media (min-width: ${media.medium}) {
      a {
        span {
          display: block;
        }
      }
    }
  `};
`;

const StyledPaginationPages = styled.ol`
  ${({ theme: { space, media } }) => css`
    display: flex;
    gap: ${space.small};
    justify-content: center;
    align-items: center;

    // Tablet > styles

    @media (min-width: ${media.medium}) {
    }
  `};
`;

// JSX

export const Pagination = ({
  onPageChange,
  linkGenerator,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  scrollToRef,
}: PaginationProps) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  // Only show pagination if there are more than one page

  if (currentPage === 0 || paginationRange?.length < 2) {
    return null;
  }

  const nextPage = currentPage + 1;
  const prevPage = currentPage - 1;

  const lastPage = paginationRange[paginationRange.length - 1];

  // Next Page

  const goToTop = () => {
    if (scrollToRef) {
      scrollToRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  };

  const handleNext = () => {
    if (onPageChange) {
      onPageChange(nextPage);
      goToTop();
    }
  };

  // Previous page

  const handlePrevious = () => {
    if (onPageChange) {
      onPageChange(prevPage);
      goToTop();
    }
  };

  return (
    <StyledPagination>
      {onPageChange && (
        <Button
          variant="primaryInverseOutline"
          arrowLeft
          a11yTitle="Previous page"
          type="button"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'propertySearchPagination',
            clickText: 'previous page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          <span>Previous</span>
        </Button>
      )}
      {linkGenerator && (
        <Button
          variant="secondaryOutline"
          arrowLeft
          a11yTitle="Previous page"
          url={linkGenerator(prevPage)}
          disabled={currentPage === 1}
          hideLabelOnMobile
          customDataLayerEvent={{
            event: 'pagination',
            clickText: 'previous page',
            paginationCurrentPage: currentPage,
            clickHostname: undefined,
            clickPath: undefined,
            clickQueryString: undefined,
            clickBladeID: undefined,
          }}
        >
          <span>Previous</span>
        </Button>
      )}
      <StyledPaginationPages>
        <Typography
          component="div"
          variant="bodyMediumBold"
        >{`${currentPage} of ${Math.ceil(totalCount / pageSize)}`}</Typography>
      </StyledPaginationPages>

      {onPageChange && (
        <Button
          variant="primaryInverseOutline"
          arrowRight
          type="button"
          a11yTitle="Next page"
          onClick={handleNext}
          disabled={lastPage === currentPage}
          hideLabelOnMobile
        >
          <span>Next</span>
        </Button>
      )}

      {linkGenerator && (
        <Button
          variant="secondaryOutline"
          arrowRight
          a11yTitle="Next page"
          url={linkGenerator(nextPage)}
          disabled={lastPage === currentPage}
          hideLabelOnMobile
        >
          <span>Next</span>
        </Button>
      )}
    </StyledPagination>
  );
};
